import { CommonModule } from '@angular/common';
import { Component, inject, input, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { getDownloadURL, StorageReference } from '@angular/fire/storage';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { fieldSorter } from '@scandium-oy/ngx-scandium';
import { map, shareReplay, take } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { PickMediaComponent } from 'src/app/components2/pick-media/pick-media.component';
import { BusinessType } from 'src/app/models/business-type.model';
import { Checklist } from 'src/app/models/checklist.model';
import { FileUpload } from 'src/app/models/file.model';
import { Instruction } from 'src/app/models/instruction.model';
import { ILanguage } from 'src/app/models/language.model';
import { SelectDialogComponent } from 'src/app/select-dialog/select.dialog';
import { BusinessTypesService } from 'src/app/services/business-types.service';
import { TicketsService } from 'src/app/services/tickets.service';
import { UploadService } from 'src/app/services/upload.service';
import { UserSessionService } from 'src/app/services/user-session.service';
import { ModalShellComponent } from 'src/app/ui/modal-shell/modal-shell.component';

@Component({
  selector: 'app-default-template-dialog',
  templateUrl: './default-template.dialog.html',
  styleUrls: ['./default-template.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    CommonModule,
    ModalShellComponent,
    PickMediaComponent,
  ],
})
export class DefaultTemplateDialogComponent implements OnInit {
  private ticketsService = inject(TicketsService);
  private uploadService = inject(UploadService);

  item = input<Checklist>();

  formGroup: FormGroup;

  fieldsArray: FormArray;
  readonly instructionImagesS = signal<string[]>([]);
  readonly instructionVideoS = signal<string | null>(null);

  businessTypes$ = this.businessTypesService.getList().pipe(
    map((types) => types.sort(fieldSorter(['name']))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  user$ = this.usersService.getCurrentUser().pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    private businessTypesService: BusinessTypesService,
    private formBuilder: FormBuilder,
    private _modal: ModalController,
    private modalCtrl: ModalController,
    private usersService: UserSessionService,
  ) {
  }

  ngOnInit(): void {
    const item = this.item();
    this.formGroup = this.formBuilder.group({
      guid: [item?.guid],
      name: [item?.name ?? '', Validators.required],
      fields: this.formBuilder.array(item?.fields.map((it) => this.formBuilder.control(it, Validators.required)) ?? []),
      contractor: [item?.contractor ?? null],
      estimate: this.formBuilder.group({
        quantity: [item?.estimate?.quantity ?? 1, Validators.required],
        unit: [item?.estimate?.unit ?? '', Validators.required],
        minutes: [item?.estimate?.minutes ?? 0, Validators.required],
      }),
      instructionExecuteText: [item?.instruction?.execute?.fi ?? ''],
      instructionImages: [item?.instruction?.quality ?? []],
      instructionVideo: [item?.instruction?.model ?? null],
    });
    this.instructionImagesS.set([...item?.instruction?.quality ?? []]);
    this.instructionVideoS.set(item?.instruction?.model ?? null);
    this.fieldsArray = this.formGroup.get('fields') as FormArray;
  }

  selectBusinessType(items: BusinessType[]) {
    const clearButton = this.formGroup.get('name').value != null;
    this.modalCtrl.create({ component: SelectDialogComponent, componentProps: { items, clearButton } }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          if (data.data.clear) {
            this.formGroup.get('guid').setValue(null);
            this.formGroup.get('name').setValue(null);
          } else {
            this.formGroup.get('guid').setValue(data.data.guid);
            this.formGroup.get('name').setValue(data.data.name);
          }
        }
      });
    });
  }

  addItem() {
    this.fieldsArray.push(this.formBuilder.control('', Validators.required));
  }

  deleteItem(index: number) {
    this.fieldsArray.removeAt(index);
  }

  translateAgain() {
    const item = this.formGroup.value;
    this.ticketsService.translateTicketList(item.guid).pipe(
      take(1),
    ).subscribe();
  }

  async onInstructionFilesSelected(files: File[]) {
    if (!files?.length) {
      return;
    }

    const uploadedImages: string[] = [];
    let uploadedVideo: string | null = null;

    for (const file of files) {
      const fileUpload = new FileUpload(file);
      fileUpload.guid = `template-instruction-${this.formGroup.get('guid').value ?? 'new'}-${file.name}-${Date.now()}`;
      if (file.type.startsWith('image/')) {
        const imageUrl = await this.uploadService.uploadImage(fileUpload);
        if (imageUrl) {
          uploadedImages.push(imageUrl);
        }
      } else if (file.type.startsWith('video/')) {
        const ref: StorageReference = await this.uploadService.uploadFile(fileUpload);
        uploadedVideo = await getDownloadURL(ref);
      }
    }

    if (uploadedImages.length > 0) {
      const nextImages = [...this.instructionImagesS(), ...uploadedImages];
      this.instructionImagesS.set(nextImages);
      this.formGroup.get('instructionImages').setValue(nextImages);
    }
    if (uploadedVideo) {
      this.instructionVideoS.set(uploadedVideo);
      this.formGroup.get('instructionVideo').setValue(uploadedVideo);
    }
  }

  removeInstructionImage(imageUrl: string) {
    const nextImages = this.instructionImagesS().filter((url) => url !== imageUrl);
    this.instructionImagesS.set(nextImages);
    this.formGroup.get('instructionImages').setValue(nextImages);
  }

  removeInstructionVideo() {
    this.instructionVideoS.set(null);
    this.formGroup.get('instructionVideo').setValue(null);
  }

  save() {
    const item = this.formGroup.value as Checklist & {
      instructionExecuteText?: string;
      instructionImages?: string[];
      instructionVideo?: string | null;
    };
    const existingInstruction = this.item()?.instruction;
    const baseInstruction = existingInstruction ?? {} as Instruction;
    const baseInstructionName = existingInstruction?.name ?? {} as ILanguage;
    const baseInstructionExecute = existingInstruction?.execute ?? {};
    const hasInstructionContent = Boolean(
      item.instructionExecuteText?.trim()
      || item.instructionImages?.length
      || item.instructionVideo,
    );
    item.instruction = hasInstructionContent
      ? {
        ...baseInstruction,
        name: {
          ...baseInstructionName,
          fi: item.name?.trim() || baseInstructionName.fi || '-',
        },
        execute: {
          ...baseInstructionExecute,
          fi: item.instructionExecuteText ?? '',
        },
        quality: [...item.instructionImages ?? []],
        model: item.instructionVideo ?? '',
      } as Instruction
      : null;
    delete item.instructionExecuteText;
    delete item.instructionImages;
    delete item.instructionVideo;

    this._modal.dismiss(item);
  }

  dismiss() {
    this._modal.dismiss();
  }
}
